import { Component, Host, h, Element, Prop, Method, Watch } from "@stencil/core";

import { StzhToastbarInitialToast, StzhToastType } from "../../index";

import Cookie from "js-cookie";

const KEY_PREFIX_CLOSED = "stzh-components-toastbar-closed";

/**
 * @slot - Slot for stzh-toast elements
 */
@Component({
  tag: "stzh-toastbar",
  styleUrl: "stzh-toastbar.scss",
  shadow: true,
})
export class StzhToastbar {
  /**
   * Time after toasts are hidden (in seconds).
   * Toasts with type `error` will stay, until the user closes them manually.
   */
  @Prop() hideAfter: number = 30;

  /**
   * Maximum of toasts allowed to be shown.
   * If more are created, oldest will be hidden, even if timeout not ended yet or toast type is `error`.
   */
  @Prop() max: number = 5;

  /**
   * Initial toasts.
   *
   * Array of objects that at least includes `label` and optionally `type`, `link` (object with `href` and `label`) `cookie` and `cookieExpires`:
   * `[{"label": "Lorem ipsum", "link": { "label": "More info", "href": "https://example.com" }, "cookie": "cookie-banner", "cookieExpires": 365 }]`
   *
   * If `cookie` exists in an object, it will check for that cookie name first (prefixed with `stzh-components-toastbar-closed-`) and only show if cookie doesn't exist.
   * `cookieExpires` sets the cookie lifetime (default 365 days, set 0 for session cookie).
   */
  @Prop() initialToasts: StzhToastbarInitialToast[] | string = [];
  private _initialToasts: StzhToastbarInitialToast[] = [];

  @Element() element: HTMLStzhToastbarElement;

  /** Create new toast. */
  @Method()
  async toast(
    label: string,
    { type, link }: { type?: StzhToastType; link?: HTMLStzhButtonElement } = { type: "info" },
  ): Promise<HTMLStzhToastElement> {
    const toast = document.createElement("stzh-toast");
    toast.hidden = true;
    toast.label = label;
    toast.role = "alert";
    toast.type = type || "info";
    toast.initialOpenAnimation = true;

    if (link) {
      link.slot = "link";
      link.size = "small";
      toast.appendChild(link);
    }

    this.element.appendChild(toast);

    return toast;
  }

  @Watch("initialToasts")
  initialToastsWatcher(newValue: any[] | string) {
    if (typeof newValue === "string") {
      this._initialToasts = JSON.parse(newValue);
    } else {
      this._initialToasts = newValue;
    }
  }

  private toastAppended(toast: HTMLStzhToastElement) {
    if (toast.getAttribute("data-stzh-toastbar-managed")) {
      return;
    }

    toast.setAttribute("data-stzh-toastbar-managed", "");

    toast.addEventListener("stzhClosed", () => {
      toast.remove();
    });

    if (toast.type !== "error") {
      let timeoutStarted = new Date().getTime();
      let restTime = this.hideAfter * 1000;
      let currentTimeout = window.setTimeout(() => {
        toast.hide();
      }, restTime);

      toast.addEventListener("pointerenter", () => {
        if (currentTimeout) {
          window.clearTimeout(currentTimeout);
          const timePassed = new Date().getTime() - timeoutStarted;
          restTime = restTime - timePassed;
        }
      });

      toast.addEventListener("pointerleave", () => {
        timeoutStarted = new Date().getTime();
        currentTimeout = window.setTimeout(() => {
          toast.hide();
        }, restTime);
      });
    }
  }

  private toastsAppended() {
    const host = (this.element.getRootNode() as ShadowRoot)?.host || this.element;
    Array.from(host.children).forEach((toast: HTMLStzhToastElement) => {
      this.toastAppended(toast);
    });
  }

  private hideOverflowingItems() {
    const host = (this.element.getRootNode() as ShadowRoot)?.host || this.element;
    const childCount = host.children.length;

    Array.from(host.children)
      .splice(0, childCount - this.max)
      .forEach((toast: HTMLStzhToastElement) => {
        toast.hide();
      });
  }

  private handleSlotchange = () => {
    this.toastsAppended();
    this.hideOverflowingItems();
  };

  componentWillLoad() {
    this.initialToastsWatcher(this.initialToasts);
  }

  componentDidLoad() {
    this._initialToasts.forEach(async data => {
      if (data.cookie) {
        const alreadyClosedBefore = Cookie.get(`${KEY_PREFIX_CLOSED}-${data.cookie}`);

        if (alreadyClosedBefore) {
          return;
        }
      }

      let link = data.link;

      if (!(link instanceof HTMLElement)) {
        const generatedLink = document.createElement("stzh-button");
        generatedLink.slot = "link";
        generatedLink.size = "small";

        Object.assign(generatedLink, data.link);
        link = generatedLink;
      }

      const toast = await this.toast(data.label, {
        type: data.type,
        link: link as HTMLStzhButtonElement,
      });

      if (data.cookie) {
        let cookieExpires = 365;

        if (data.cookieExpires === 0) {
          cookieExpires = null;
        } else if (data.cookieExpires) {
          cookieExpires = data.cookieExpires;
        }

        toast.addEventListener("stzhClosed", () => {
          Cookie.set(`${KEY_PREFIX_CLOSED}-${data.cookie}`, "1", {
            expires: cookieExpires,
          });
        });
      }
    });
  }

  render() {
    const classes = {
      "stzh-toastbar": true,
    };

    return (
      <Host>
        <div class={classes}>
          <slot onSlotchange={this.handleSlotchange}></slot>
        </div>
      </Host>
    );
  }
}
